/*
 * Sonic Bonk : rendu « soigné ».
 * Ciel dégradé avec soleil, mer animée (vagues, écume, reflets), herbe qui ondule,
 * décor plus détaillé, particules douces et post-traitement (bloom), avec qualité adaptative.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

export const SKY_TOP = new THREE.Color('#1f6fe0');
export const SKY_HORIZON = new THREE.Color('#9fd6ff');
export const SUN_DIR = new THREE.Vector3(0.45, 0.62, 0.35).normalize();

const rand = (a, b) => a + Math.random() * (b - a);

/* ——— Géométries colorées (même principe que le moteur) ——— */
const M = (x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx) => new THREE.Matrix4().compose(
  new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), new THREE.Vector3(sx, sy, sz)
);
const tint = (geo, colorFn) => {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (g.attributes.uv) g.deleteAttribute('uv');
  const p = g.attributes.position;
  const arr = new Float32Array(p.count * 3);
  const c = new THREE.Color();
  for (let i = 0; i < p.count; i += 1) {
    colorFn(c, p.getX(i), p.getY(i), p.getZ(i), i);
    arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  if (g.attributes.normal) g.deleteAttribute('normal');
  g.computeVertexNormals();
  return g;
};
const solid = (geo, color, m) => { const g = (geo.index ? geo.toNonIndexed() : geo); if (m) g.applyMatrix4(m); return tint(g, (c) => c.set(color)); };

/** Palmier : tronc courbé en anneaux, palmes retombantes en deux tons, noix de coco. */
export const palmGeo = () => {
  const parts = [];
  const seg = 9;
  const top = new THREE.Vector3();
  for (let i = 0; i < seg; i += 1) {
    const t = i / seg;
    const x = t * t * 1.4; const y = 0.35 + i * 0.72;
    const r = 0.27 - t * 0.1;
    const g = new THREE.CylinderGeometry(r * 0.92, r, 0.74, 7);
    g.applyMatrix4(M(x, y, 0, 0, 0, -t * 0.32));
    parts.push(tint(g, (c, px, py) => c.set(i % 2 ? '#9c5f34' : '#b87442').offsetHSL(0, 0, (py - y) * 0.08)));
    top.set(x + 0.1, y + 0.4, 0);
  }
  const fronds = 9;
  for (let k = 0; k < fronds; k += 1) {
    const a = (k / fronds) * Math.PI * 2 + rand(-0.15, 0.15);
    const lift = k % 2 ? 0.1 : -0.05;
    // palme : trois segments effilés qui partent du sommet puis retombent en arc
    let px = 0; let py = 0;
    for (let sgm = 0; sgm < 3; sgm += 1) {
      const len = 1.05;
      const w0 = 0.62 - sgm * 0.16; const w1 = Math.max(0.08, w0 - 0.16);
      const d = -lift + 0.12 + sgm * 0.42; // angle vers le bas
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, -w0 / 2, len, 0, -w1 / 2, len, 0, w1 / 2, 0, 0, -w0 / 2, len, 0, w1 / 2, 0, 0, w0 / 2], 3));
      g.applyMatrix4(new THREE.Matrix4().makeRotationZ(-d));
      g.applyMatrix4(new THREE.Matrix4().makeTranslation(px, py, 0));
      px += Math.cos(d) * len; py -= Math.sin(d) * len;
      g.applyMatrix4(new THREE.Matrix4().makeRotationY(-a));
      g.applyMatrix4(new THREE.Matrix4().makeTranslation(top.x, top.y, top.z));
      const shade = k % 2 ? '#2f9e45' : '#45bd5a';
      parts.push(tint(g, (c, x, y) => c.set(shade).offsetHSL(0, 0.04, sgm * 0.03 + (y - top.y) * 0.05)));
    }
  }
  [[0.22, -0.3, 0.12], [-0.18, -0.32, -0.16], [0.05, -0.36, -0.24]].forEach(([dx, dy, dz]) => parts.push(solid(new THREE.SphereGeometry(0.2, 6, 5), '#6b3d20', M(top.x + dx, top.y + dy, top.z + dz))));
  return mergeGeometries(parts, false);
};

/** Rocher : icosaèdre bosselé, plus clair en haut. */
export const rockGeo = () => {
  const g = new THREE.IcosahedronGeometry(1, 1);
  const p = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i += 1) {
    v.fromBufferAttribute(p, i);
    const n = 1 + Math.sin(v.x * 3.1 + v.z * 2.3) * 0.12 + Math.cos(v.y * 4.2 + v.x * 1.7) * 0.1;
    v.multiplyScalar(n); v.y *= 0.72;
    p.setXYZ(i, v.x, v.y + 0.35, v.z);
  }
  return tint(g, (c, x, y) => c.set('#8d8a9c').lerp(new THREE.Color('#c9c6d6'), Math.min(1, Math.max(0, y * 0.7))));
};

/** Buisson : grappe de sphères vertes. */
export const bushGeo = () => mergeGeometries([
  [0, 0.55, 0, 0.75], [0.6, 0.42, 0.15, 0.55], [-0.55, 0.4, -0.1, 0.58], [0.1, 0.45, 0.55, 0.5], [-0.15, 0.85, -0.2, 0.5]
].map(([x, y, z, r]) => {
  const g = new THREE.IcosahedronGeometry(r, 1);
  g.applyMatrix4(M(x, y, z));
  return tint(g, (c, px, py) => c.set('#2f9e45').lerp(new THREE.Color('#6fd65a'), Math.min(1, Math.max(0, (py - 0.1) * 0.9))));
}), false);

/** Fleur : tige, pétales en couronne, cœur. */
export const flowerGeo = (petal = '#ffd23a') => mergeGeometries([
  solid(new THREE.CylinderGeometry(0.04, 0.05, 1.1, 5), '#2f8a3a', M(0, 0.55, 0)),
  ...Array.from({ length: 8 }, (_, i) => solid(new THREE.SphereGeometry(0.15, 6, 4), petal, M(Math.cos(i * 0.785) * 0.22, 1.15, Math.sin(i * 0.785) * 0.22, 0, 0, 0, 1, 0.35, 0.7).multiply(new THREE.Matrix4().makeRotationY(-i * 0.785)))),
  solid(new THREE.CylinderGeometry(0.13, 0.13, 0.1, 8), '#7a3a10', M(0, 1.18, 0)),
  solid(new THREE.SphereGeometry(0.16, 5, 3), '#3fc45a', M(0.2, 0.5, 0, 0, 0, 0, 1, 0.25, 0.6))
], false);

/** Nuage : grappe de sphères douces. */
export const cloudGeo = () => mergeGeometries([
  [0, 0, 0, 3.2], [2.9, -0.5, 0.4, 2.5], [-2.8, -0.6, -0.2, 2.3], [1.2, 1.5, 0.3, 2.1], [-1.1, 1.2, -0.4, 1.9], [4.6, -1.1, 0, 1.6], [-4.4, -1.2, 0.2, 1.5]
].map(([x, y, z, r]) => {
  const g = new THREE.IcosahedronGeometry(r, 2);
  g.applyMatrix4(M(x, y, z, 0, 0, 0, 1, 0.82, 1));
  return tint(g, (c, px, py) => c.set('#e6f2ff').lerp(new THREE.Color('#ffffff'), Math.min(1, Math.max(0, (py + 1.5) / 4))));
}), false);

/* ——— Ciel : dôme dégradé + halo du soleil ——— */
export const createSky = () => {
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: { top: { value: SKY_TOP }, horizon: { value: SKY_HORIZON }, sun: { value: SUN_DIR } },
    vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); vec4 p = modelViewMatrix * vec4(position, 1.0); gl_Position = projectionMatrix * p; gl_Position.z = gl_Position.w; }',
    fragmentShader: `
      uniform vec3 top; uniform vec3 horizon; uniform vec3 sun; varying vec3 vDir;
      void main(){
        float h = clamp(vDir.y, -0.2, 1.0);
        vec3 col = mix(horizon, top, pow(max(h, 0.0), 0.55));
        col = mix(col, horizon * 0.92, smoothstep(0.0, -0.2, h));
        float s = max(dot(normalize(vDir), sun), 0.0);
        col += vec3(1.0, 0.92, 0.72) * (pow(s, 900.0) * 6.0 + pow(s, 24.0) * 0.35 + pow(s, 4.0) * 0.08);
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(400, 32, 16), mat);
  mesh.frustumCulled = false;
  mesh.renderOrder = -10;
  return mesh;
};

/* ——— Mer : vagues, dégradé lagon → large, écume au bord de l'île, reflets ——— */
export const createWater = (islandR, heights, n1, size, level) => {
  // Hauteurs du terrain autour du niveau de la mer (±3 m) dans une texture : écume et lagon suivent la vraie côte.
  const data = new Uint8Array(n1 * n1);
  for (let i = 0; i < n1 * n1; i += 1) data[i] = Math.round(Math.min(1, Math.max(0, (heights[i] - level + 3) / 6)) * 255);
  const tex = new THREE.DataTexture(data, n1, n1, THREE.RedFormat, THREE.UnsignedByteType);
  tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearFilter; tex.needsUpdate = true;
  const uniforms = { uTime: { value: 0 }, uIsland: { value: islandR }, uHeight: { value: tex }, uSize: { value: size } };
  const mat = new THREE.MeshStandardMaterial({ color: '#2a8be0', roughness: 0.18, metalness: 0.05, transparent: true, opacity: 0.92 });
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, uniforms);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime; varying vec3 vWPos;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vec4 wp0 = modelMatrix * vec4(transformed, 1.0);
        float wv = sin(wp0.x * 0.18 + uTime * 1.3) * 0.12 + sin(wp0.z * 0.23 - uTime * 1.1) * 0.1 + sin((wp0.x + wp0.z) * 0.41 + uTime * 2.1) * 0.05;
        transformed.z += wv;`)
      .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform float uTime; uniform float uIsland; uniform sampler2D uHeight; uniform float uSize; varying vec3 vWPos;
        float h21(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float vn(vec2 p){ vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h21(i), h21(i + vec2(1, 0)), f.x), mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), f.x), f.y); }`)
      .replace('#include <color_fragment>', `#include <color_fragment>
        vec2 huv = vWPos.xz / uSize + 0.5;
        float inside = step(0.0, huv.x) * step(huv.x, 1.0) * step(0.0, huv.y) * step(huv.y, 1.0);
        float th = mix(0.0, texture2D(uHeight, huv).r, inside) * 6.0 - 3.0; // hauteur du sol par rapport à la mer
        float depth = max(0.0, -th);
        float n = vn(vWPos.xz * 0.35 + vec2(uTime * 0.25, uTime * 0.18)) * 0.6 + vn(vWPos.xz * 0.9 - uTime * 0.35) * 0.4;
        vec3 deep = vec3(0.04, 0.28, 0.66); vec3 mid = vec3(0.08, 0.52, 0.82); vec3 shallow = vec3(0.3, 0.84, 0.86);
        vec3 col = mix(shallow, mid, smoothstep(0.2, 1.6, depth));
        col = mix(col, deep, smoothstep(1.6, 3.0, depth) * inside + (1.0 - inside));
        float shore = 1.0 - smoothstep(0.05, 0.45 + n * 0.25, depth);
        float waveLine = smoothstep(0.75, 0.95, sin(depth * 9.0 - uTime * 2.4) * 0.5 + 0.5) * (1.0 - smoothstep(0.3, 1.1, depth));
        float foam = clamp(shore * 0.85 + waveLine * 0.45 * smoothstep(0.35, 0.7, n), 0.0, 1.0);
        float sparkle = smoothstep(0.86, 0.97, vn(vWPos.xz * 1.6 + uTime * 0.6)) * 0.25;
        diffuseColor.rgb = mix(col, vec3(1.0), clamp(foam + sparkle, 0.0, 1.0));`);
  };
  const geo = new THREE.PlaneGeometry(900, 900, 120, 120);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  return { mesh, uniforms, mat, geo, tex };
};

/* ——— Herbe : touffes instanciées qui ondulent au vent ——— */
export const createGrass = (spots) => {
  const blade = (ang, h, lean) => {
    const g = new THREE.BufferGeometry();
    const w = 0.09;
    g.setAttribute('position', new THREE.Float32BufferAttribute([-w, 0, 0, w, 0, 0, lean * 0.6, h, lean], 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute([0.3, 0.58, 0.2, 0.3, 0.58, 0.2, 0.66, 0.95, 0.4], 3));
    g.applyMatrix4(new THREE.Matrix4().makeRotationY(ang));
    g.computeVertexNormals();
    return g;
  };
  const tuft = mergeGeometries([blade(0, 0.55, 0.12), blade(1.1, 0.45, -0.1), blade(2.2, 0.6, 0.08), blade(3.1, 0.4, 0.14), blade(4.2, 0.5, -0.12)], false);
  // Les normales pointent vers le haut : l'herbe prend la lumière comme le sol
  const n = tuft.attributes.normal;
  for (let i = 0; i < n.count; i += 1) n.setXYZ(i, 0, 1, 0);
  const uniforms = { uTime: { value: 0 } };
  const mat = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, uniforms);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        #ifdef USE_INSTANCING
          vec3 ip = vec3(instanceMatrix[3][0], instanceMatrix[3][1], instanceMatrix[3][2]);
        #else
          vec3 ip = vec3(0.0);
        #endif
        float sway = sin(uTime * 2.2 + ip.x * 0.35 + ip.z * 0.25) * 0.5 + sin(uTime * 3.7 + ip.x * 0.9) * 0.2;
        transformed.x += sway * position.y * 0.32;
        transformed.z += sway * position.y * 0.18;`);
  };
  const mesh = new THREE.InstancedMesh(tuft, mat, spots.length);
  const m = new THREE.Matrix4(); const q = new THREE.Quaternion(); const e = new THREE.Euler(); const v = new THREE.Vector3(); const s = new THREE.Vector3();
  spots.forEach((p, i) => {
    e.set(0, rand(0, Math.PI * 2), 0); q.setFromEuler(e);
    const k = rand(0.5, 1.0);
    v.set(p.x, p.y, p.z); s.set(k, k * rand(0.8, 1.3), k);
    m.compose(v, q, s);
    mesh.setMatrixAt(i, m);
    const c = new THREE.Color().setHSL(0.26 + rand(-0.02, 0.03), 0.45, 0.5 + rand(-0.05, 0.07));
    mesh.setColorAt(i, c.multiplyScalar(1.7));
  });
  mesh.receiveShadow = true;
  return { mesh, uniforms, geo: tuft, mat };
};

/* ——— Particules : point rond et doux ——— */
export const softDot = () => {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const x = c.getContext('2d');
  const gr = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.35, 'rgba(255,255,255,0.85)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = gr; x.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

/* ——— Post-traitement + qualité adaptative ——— */
export const createPostFX = (renderer, scene, camera) => {
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), 0.42, 0.55, 0.86);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());
  const q = { level: 2, samples: [], sum: 0 }; // 2 = bloom + ombres, 1 = ombres, 0 = minimal
  return {
    composer,
    bloom,
    setSize: (w, h, pr) => { composer.setPixelRatio(pr); composer.setSize(w, h); },
    render: (dt) => {
      if (q.level >= 2) composer.render(dt); else renderer.render(scene, camera);
    },
    // Si la machine peine (plus de 28 ms par image en moyenne sur 4 s), on baisse d'un cran.
    sample: (frameMs, onDrop) => {
      q.samples.push(frameMs); q.sum += frameMs;
      if (q.samples.length > 240) q.sum -= q.samples.shift();
      if (q.samples.length === 240 && q.sum / 240 > 28 && q.level > 0) {
        q.level -= 1; q.samples = []; q.sum = 0;
        onDrop(q.level);
      }
    },
    get level() { return q.level; },
    dispose: () => { composer.dispose?.(); bloom.dispose?.(); }
  };
};
